import { motion } from 'framer-motion';
import { FlaskConical, FileText, Award, TrendingUp, Atom, Layers, BarChart3, Dna, Calculator, BookOpen, ArrowRight, ExternalLink, ChevronRight } from 'lucide-react';
import Layout from '@/components/Layout';
import { IMAGES } from '@/assets/images';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const researchStats = [
  { label: '年度科研经费', value: '32.6', unit: '亿元', icon: TrendingUp, color: 'text-chart-1' },
  { label: '年度论文发表', value: '8,540', unit: '篇', icon: FileText, color: 'text-primary' },
  { label: '授权专利', value: '1,286', unit: '项', icon: Award, color: 'text-chart-3' },
  { label: '国家级奖项', value: '47', unit: '项', icon: FlaskConical, color: 'text-chart-5' },
];

const researchAreas = [
  {
    name: '化学科学',
    englishName: 'Chemical Sciences',
    icon: FlaskConical,
    description: '南开化学享誉国际，在有机合成、功能材料、绿色化学等方向居国际前沿，拥有国家重点实验室3个。',
    highlights: ['有机功能材料', '绿色催化合成', '超分子化学', '药物化学'],
    level: '国家重点学科',
  },
  {
    name: '材料科学',
    englishName: 'Materials Science',
    icon: Layers,
    description: '南开材料学科在纳米材料、能源材料、光电材料等领域取得系列重要成果，多项成果转化应用于产业。',
    highlights: ['纳米功能材料', '能源存储材料', '有机光电材料', '生物医用材料'],
    level: '双一流学科',
  },
  {
    name: '经济学',
    englishName: 'Economics',
    icon: BarChart3,
    description: '南开经济学科是中国现代经济学研究的摇篮，在宏观经济、国际贸易、发展经济学等领域具有重要影响力。',
    highlights: ['宏观经济理论', '国际贸易政策', '数字经济', '双碳经济学'],
    level: '国家重点学科',
  },
  {
    name: '数学',
    englishName: 'Mathematics',
    icon: Calculator,
    description: '继承陈省身先生传统，南开数学在代数拓扑、微分几何、数论等纯粹数学方向及应用数学领域均享有盛誉。',
    highlights: ['代数几何', '微分拓扑', '数论', '数学物理'],
    level: '双一流学科',
  },
  {
    name: '物理学',
    englishName: 'Physics',
    icon: Atom,
    description: '南开物理在凝聚态物理、量子信息、粒子物理等前沿方向持续产出高影响力成果，承担多项国家重大科研任务。',
    highlights: ['凝聚态物理', '量子计算', '光量子通信', '粒子与核物理'],
    level: '国家重点学科',
  },
  {
    name: '生命科学',
    englishName: 'Life Sciences',
    icon: Dna,
    description: '南开生命科学在细胞信号转导、基因调控、生物医药等方向取得重要突破，为人类健康事业做出积极贡献。',
    highlights: ['细胞信号转导', '基因组学', '合成生物学', '抗肿瘤药物'],
    level: '双一流学科',
  },
];

const researchInstitutes = [
  { name: '元素有机化学国家重点实验室', category: '国家级', founded: 1984 },
  { name: '药物化学生物学国家重点实验室', category: '国家级', founded: 2011 },
  { name: '天津化学化工协同创新中心', category: '协同创新', founded: 2013 },
  { name: '南开大学陈省身数学研究所', category: '研究所', founded: 1985 },
  { name: '南开大学经济行为与政策模拟实验室', category: '省部级', founded: 2008 },
  { name: '光电信息技术科学教育部重点实验室', category: '教育部级', founded: 2000 },
  { name: '南开大学泰达应用物理研究院', category: '研究院', founded: 2003 },
  { name: '天津市生物活性材料重点实验室', category: '省部级', founded: 2006 },
  { name: '南开大学人工智能学院研究中心', category: '校级', founded: 2019 },
  { name: '南开大学国家大学生文化素质教育基地', category: '国家级', founded: 1999 },
];

const achievements = [
  {
    title: '化学学院团队在《Nature》发表有机光电材料重大成果',
    category: '学术突破',
    date: '2026-04-15',
    description: '南开化学功能材料团队历时五年攻关，在高效有机太阳能电池材料领域取得重大突破，研究成果发表于《Nature》期刊。',
    journal: 'Nature',
    impact: 'IF: 69.5',
  },
  {
    title: '物理学院量子计算研究取得新进展，实现百量子比特纠缠',
    category: '前沿探索',
    date: '2026-03-22',
    description: '南开量子信息研究团队成功实现百量子比特高保真度纠缠态制备，相关成果发表于《Science》期刊，引发国际广泛关注。',
    journal: 'Science',
    impact: 'IF: 63.8',
  },
  {
    title: '数学科学学院王教授荣获2026年陈省身数学奖',
    category: '学术荣誉',
    date: '2026-03-10',
    description: '数学科学学院王教授因在代数几何领域做出的杰出贡献，荣获中国数学会颁发的2026年陈省身数学奖，是南开数学传统的最好传承。',
    journal: '中国数学会',
    impact: '最高学术荣誉',
  },
  {
    title: '环境学院双碳研究成果获国家科技进步一等奖',
    category: '国家奖项',
    date: '2026-02-28',
    description: '南开环境科学与工程学院在碳捕获与封存技术方面的系统性研究成果，荣获2025年度国家科技进步一等奖。',
    journal: '国家科学技术奖励办公室',
    impact: '国家一等奖',
  },
  {
    title: '生命科学学院在肿瘤免疫治疗机制研究中取得重要突破',
    category: '学术突破',
    date: '2026-02-10',
    description: '南开生命科学学院研究团队揭示了肿瘤微环境中免疫逃逸的关键分子机制，为新型肿瘤免疫治疗策略的开发提供了重要理论基础。',
    journal: 'Cell',
    impact: 'IF: 66.9',
  },
];

const categoryColors: Record<string, string> = {
  '学术突破': 'bg-primary/10 text-primary',
  '前沿探索': 'bg-chart-1/20 text-chart-1',
  '学术荣誉': 'bg-chart-4/20 text-chart-4',
  '国家奖项': 'bg-destructive/10 text-destructive',
};

const instituteCategoryColors: Record<string, string> = {
  '国家级': 'bg-primary text-primary-foreground',
  '协同创新': 'bg-chart-1/20 text-chart-1',
  '研究所': 'bg-secondary text-secondary-foreground',
  '省部级': 'bg-accent text-accent-foreground',
  '教育部级': 'bg-chart-4/20 text-chart-4',
  '研究院': 'bg-chart-5/20 text-chart-5',
  '校级': 'bg-muted text-muted-foreground',
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const staggerContainer = {
  initial: {},
  whileInView: {},
  viewport: { once: true },
};

export default function Research() {
  return (
    <Layout>
      <div className="relative overflow-hidden">
        <div className="relative h-72 md:h-96 overflow-hidden">
          <img
            src={IMAGES.RESEARCH_5}
            alt="科学研究"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-sidebar/80 via-sidebar/60 to-sidebar/90" />
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              className="text-center text-white px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-sidebar-foreground/70 text-sm font-medium tracking-widest uppercase mb-3">Scientific Research</p>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                科学研究
              </h1>
              <p className="text-sidebar-foreground/80 text-lg max-w-2xl mx-auto">
                追求真知、服务社会，南开大学持续推动前沿科学研究与技术创新
              </p>
            </motion.div>
          </div>
        </div>

        <div className="bg-sidebar">
          <div className="container mx-auto px-4 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {researchStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="text-center"
                  >
                    <div className="flex justify-center mb-3">
                      <div className="w-12 h-12 rounded-full bg-sidebar-accent flex items-center justify-center">
                        <Icon className={`w-6 h-6 ${stat.color}`} />
                      </div>
                    </div>
                    <div className="font-mono text-3xl font-bold text-white mb-1">
                      {stat.value}
                      <span className="text-lg ml-1 text-sidebar-foreground/70">{stat.unit}</span>
                    </div>
                    <p className="text-sidebar-foreground/60 text-sm">{stat.label}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <motion.div className="text-center mb-14" {...fadeInUp}>
              <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">Research Areas</p>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                重点研究领域
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                南开大学在多个学科领域保持国内外领先地位，产出系列具有重要国际影响力的科研成果
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {researchAreas.map((area, index) => {
                const Icon = area.icon;
                return (
                  <motion.div
                    key={area.name}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.08, duration: 0.5 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-all duration-300 hover:border-primary/30 group">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {area.level}
                          </Badge>
                        </div>
                        <CardTitle className="text-lg text-foreground">{area.name}</CardTitle>
                        <p className="text-xs text-muted-foreground">{area.englishName}</p>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                          {area.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {area.highlights.map((h) => (
                            <span
                              key={h}
                              className="text-xs px-2 py-1 rounded-md bg-accent text-accent-foreground"
                            >
                              {h}
                            </span>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div {...fadeInUp}>
                <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">Innovation Highlights</p>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                  科技创新高地
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  南开大学以建设世界一流大学为目标，持续加强科研平台建设，培育重大原创成果。学校现有国家重点实验室3个、教育部重点实验室12个、国家工程中心2个，为科技创新提供坚实支撑。
                </p>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  近五年来，学校累计获得国家自然科学基金项目1580余项，承担国家重点研发计划项目86项，科研经费总量持续增长，年均增速超过15%。
                </p>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-card rounded-xl p-4 border border-border">
                    <div className="font-mono text-2xl font-bold text-chart-1 mb-1">3</div>
                    <p className="text-sm text-muted-foreground">国家重点实验室</p>
                  </div>
                  <div className="bg-card rounded-xl p-4 border border-border">
                    <div className="font-mono text-2xl font-bold text-primary mb-1">12</div>
                    <p className="text-sm text-muted-foreground">教育部重点实验室</p>
                  </div>
                  <div className="bg-card rounded-xl p-4 border border-border">
                    <div className="font-mono text-2xl font-bold text-chart-3 mb-1">86</div>
                    <p className="text-sm text-muted-foreground">国家重点研发项目</p>
                  </div>
                  <div className="bg-card rounded-xl p-4 border border-border">
                    <div className="font-mono text-2xl font-bold text-chart-5 mb-1">15%+</div>
                    <p className="text-sm text-muted-foreground">科研经费年增速</p>
                  </div>
                </div>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                  了解更多科研平台
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={IMAGES.RESEARCH_7}
                    alt="科研实验室"
                    className="w-full h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-sidebar/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-card/90 backdrop-blur-sm rounded-xl p-4 border border-border">
                      <p className="text-xs text-muted-foreground mb-1">最新成果</p>
                      <p className="text-sm font-semibold text-foreground">
                        化学学院功能材料团队在《Nature》发表重要研究
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-6 -right-6 w-32 h-32 rounded-2xl overflow-hidden shadow-xl border-4 border-card">
                  <img
                    src={IMAGES.LIBRARY_3}
                    alt="学术研究"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <motion.div className="text-center mb-14" {...fadeInUp}>
              <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">Research Institutes</p>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                科研机构
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                汇聚国家级、省部级重点实验室及高水平研究机构，打造世界一流科研生态
              </p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              variants={staggerContainer}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true }}
            >
              {researchInstitutes.map((institute, index) => (
                <motion.div
                  key={institute.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                  className="flex items-center justify-between p-4 bg-card border border-border rounded-xl hover:border-primary/30 hover:shadow-md transition-all duration-200 group cursor-pointer"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-foreground text-sm truncate">{institute.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">成立于 {institute.founded} 年</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <span className={`text-xs px-2 py-1 rounded-md font-medium ${instituteCategoryColors[institute.category] || 'bg-muted text-muted-foreground'}`}>
                      {institute.category}
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div className="text-center mb-14" {...fadeInUp}>
              <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">Recent Achievements</p>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                近期学术成果
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                南开大学科研团队持续在国际顶尖学术期刊发表高水平研究成果
              </p>
            </motion.div>

            <div className="space-y-4">
              {achievements.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                >
                  <Card className="hover:shadow-md transition-all duration-200 hover:border-primary/20 group cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        <div className="flex items-center gap-3 md:w-auto flex-shrink-0">
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${categoryColors[item.category] || 'bg-muted text-muted-foreground'}`}>
                            {item.category}
                          </span>
                          <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">{item.date}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors leading-snug">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                        <div className="flex flex-row md:flex-col items-start gap-2 md:gap-1 flex-shrink-0 md:text-right">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <BookOpen className="w-3.5 h-3.5" />
                            <span>{item.journal}</span>
                          </div>
                          <div className="text-xs font-medium text-chart-1">{item.impact}</div>
                        </div>
                        <div className="hidden md:flex items-center">
                          <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="text-center mt-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/5">
                查看全部学术成果
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          </div>
        </section>

        <section className="py-16 bg-sidebar">
          <div className="container mx-auto px-4">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                加入南开科研团队
              </h2>
              <p className="text-sidebar-foreground/70 mb-8 max-w-xl mx-auto">
                我们欢迎有志于科学研究的学者、博士后、研究生加入，共同探索知识前沿
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-chart-1 text-white hover:bg-chart-1/90" size="lg">
                  科研人员招聘
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" className="border-sidebar-foreground/30 text-sidebar-foreground hover:bg-sidebar-accent" size="lg">
                  合作交流申请
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
